import { CompanionSize } from './CompanionTypes';

export const DEFAULT_NATIVE_SIZES: CompanionSize[] = [
  { width: 320, height: 50 },
  { width: 300, height: 250 },
];

export interface GptSlots {
  /** Sizes of the slots IMA has filled with a companion. */
  filled(throughRevision?: number): CompanionSize[];
  /** Shows the filled slot that fits `containerWidth` (or hides all of them). Returns the shown size. */
  show(visible: boolean, containerWidth: number, throughRevision?: number): CompanionSize | undefined;
  /** Removes any rendered creative so a stale one cannot show. */
  clear(throughRevision?: number): void;
  /** Monotonic fill revision, including replacement creatives in already-filled slots. */
  revision(): number;
  /** Fill revision of the slot made visible by the latest `show`, or undefined when none is visible. */
  shownRevision(): number | undefined;
  destroy(): void;
}

export interface GptSlotsOptions {
  root: HTMLElement;
  adUnitPath: string;
  googletag: any;
  sizes: CompanionSize[];
  onFillChanged: () => void;
}

let nextId = 0;

/**
 * Registers SDK-owned Google Publisher Tag companion slots. IMA renders the companion, handles clicks and
 * fires the beacons; the SDK only owns the slot elements and decides when they are visible.
 */
export function createGptSlots(options: GptSlotsOptions): GptSlots {
  const { root, adUnitPath, googletag, sizes, onFillChanged } = options;
  const prefix = `nxg-companion-${++nextId}`;
  const entries = sizes.map(size => {
    const element = document.createElement('div');
    element.id = `${prefix}-${size.width}x${size.height}`;
    element.dataset.companionSlot = `${size.width}x${size.height}`;
    Object.assign(element.style, {
      display: 'none',
      width: `${size.width}px`,
      height: `${size.height}px`,
      maxWidth: '100%',
      overflow: 'hidden',
      margin: '0 auto',
    });
    return { size, element, slot: undefined as any, filled: false, revision: 0 };
  });
  const wrapper = document.createElement('div');
  wrapper.dataset.companion = 'standard';
  Object.assign(wrapper.style, { display: 'none', width: '100%', textAlign: 'center' });
  entries.forEach(entry => wrapper.append(entry.element));
  root.append(wrapper);

  let destroyed = false;
  let fillRevision = 0;
  let shownFillRevision: number | undefined;

  const onSlotRenderEnded = (event: any): void => {
    const id = event?.slot?.getSlotElementId?.();
    const entry = entries.find(e => e.element.id === id);
    if (!entry || destroyed) {
      return;
    }
    const filled = !event.isEmpty;
    if (filled) entry.revision = ++fillRevision;
    if (filled || filled !== entry.filled) {
      entry.filled = filled;
      onFillChanged();
    }
  };

  const clearSlots = (throughRevision = Infinity): void => {
    const cleared = entries.filter(e => e.revision <= throughRevision);
    cleared.forEach(e => (e.filled = false));
    const slots = cleared.map(e => e.slot).filter(Boolean);
    if (slots.length) {
      try {
        googletag.pubads().clear(slots);
      } catch {
        // Clearing is best effort; the elements are hidden regardless.
      }
    }
  };

  googletag.cmd.push(() => {
    if (destroyed) {
      return;
    }
    entries.forEach(entry => {
      entry.slot =
        googletag.defineSlot(adUnitPath, [entry.size.width, entry.size.height], entry.element.id) ?? undefined;
      // Slots stay off PubAdsService, so display() cannot request ordinary display inventory for them.
      entry.slot?.addService(googletag.companionAds());
    });
    // Only companions from the video ad may fill the slots, never a network backfill ad.
    googletag.companionAds().setRefreshUnfilledSlots(false);
    googletag.pubads().enableVideoAds();
    // IMA renders through CompanionAdsService. PubAdsService reports separate display requests.
    googletag.companionAds().addEventListener('slotRenderEnded', onSlotRenderEnded);
    googletag.enableServices();
    entries.forEach(entry => entry.slot && googletag.display(entry.element.id));
  });

  return {
    revision: () => fillRevision,
    shownRevision: () => shownFillRevision,
    filled: (throughRevision = Infinity) =>
      entries.filter(e => e.filled && e.revision <= throughRevision).map(e => e.size),
    show: (visibleNow, containerWidth, throughRevision = Infinity) => {
      const filled = entries.filter(e => e.filled && e.revision <= throughRevision);
      const fitting = filled.filter(e => !containerWidth || e.size.width <= containerWidth);
      // Largest slot that fits the container; if none fits, any filled slot is better than nothing.
      const area = (e: (typeof entries)[number]): number => e.size.width * e.size.height;
      const largestFitting = [...fitting].sort((a, b) => area(b) - area(a))[0];
      const chosen = visibleNow ? (largestFitting ?? filled[0]) : undefined;
      shownFillRevision = chosen?.revision;
      entries.forEach(e => (e.element.style.display = e === chosen ? 'block' : 'none'));
      wrapper.style.display = chosen ? 'block' : 'none';
      return chosen?.size;
    },
    clear: throughRevision => {
      if (destroyed) {
        return;
      }
      clearSlots(throughRevision);
      entries.forEach(e => (e.element.style.display = 'none'));
      wrapper.style.display = 'none';
    },
    destroy: () => {
      if (destroyed) {
        return;
      }
      clearSlots();
      destroyed = true;
      const slots = entries.map(e => e.slot).filter(Boolean);
      try {
        googletag.companionAds().removeEventListener?.('slotRenderEnded', onSlotRenderEnded);
        if (slots.length) {
          googletag.destroySlots(slots);
        }
      } catch {
        // Teardown must not throw.
      }
      wrapper.remove();
    },
  };
}
